import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@design/Card';
import { Button } from '@design/Button';
import { Callout } from '@design/Callout';
import { useEventBus } from '@bus/useEventBus';
import { ACCOUNTS, DEMO_SCENARIO, PRESETS, accountById, debitIncreases } from './data';

type Entry = {
  id: string;
  description: string;
  debit: string;   // account id
  credit: string;  // account id
  amount: number;
};

type Posted = Entry & { ts: number };

const SIM_ID = 'accounting-cycle-v1';

// ── Step definitions ──────────────────────────────────────────────────────────
type Step = 'transactions' | 'journal' | 'ledger' | 'trial-balance';
const STEPS: { id: Step; label: string; shortLabel: string }[] = [
  { id: 'transactions',   label: 'Transactions',  shortLabel: '1. Transactions'  },
  { id: 'journal',        label: 'Journal',        shortLabel: '2. Journal'        },
  { id: 'ledger',         label: 'Ledger',         shortLabel: '3. Ledger'         },
  { id: 'trial-balance',  label: 'Trial Balance',  shortLabel: '4. Trial Balance'  },
];

// ── Ledger types ──────────────────────────────────────────────────────────────
type LedgerSide = { entryId: string; counter: string; amount: number };
type Ledger = { debits: LedgerSide[]; credits: LedgerSide[]; balance: number };

// ── Trial Balance row ─────────────────────────────────────────────────────────
type TBRow = {
  acctId: string;
  name: string;
  type: string;
  drBalance: number; // placed in debit column (0 if credit-side)
  crBalance: number; // placed in credit column (0 if debit-side)
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  chapterId?: string;
  /** When true: pre-loads the demo scenario, hides the presets bar, shows a compact header */
  embedded?: boolean;
};

export function JournalLedgerSimulation({ chapterId, embedded = false }: Props) {
  const emit = useEventBus();

  // Active step in the 4-step cycle
  const [step, setStep] = useState<Step>(embedded ? 'transactions' : 'journal');

  // Free-play journal entries (only in non-embedded chapter mode)
  const [draft, setDraft] = useState<Entry>(emptyDraft());
  const [entries, setEntries] = useState<Posted[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Demo scenario walkthrough state (used in embedded mode + transactions step)
  const [demoPosted, setDemoPosted] = useState<number>(0); // how many demo txns revealed
  const [tbConfirmed, setTbConfirmed] = useState<Set<string>>(new Set()); // acctIds confirmed by student

  useEffect(() => {
    emit({ kind: 'sim.mount', payload: { simId: SIM_ID, chapterId: chapterId ?? 'demo' } });
  }, [emit, chapterId]);

  // In embedded mode, the "entries" are derived from the demo scenario
  const activeEntries: Posted[] = useMemo(() => {
    if (embedded) {
      return DEMO_SCENARIO.slice(0, demoPosted).map((s, i) => ({
        ...s,
        ts: i,
      }));
    }
    return entries;
  }, [embedded, demoPosted, entries]);

  const ledgers = useMemo(() => buildLedgers(activeEntries), [activeEntries]);
  const trialBalanceRows = useMemo(() => buildTrialBalance(ledgers), [ledgers]);

  const debitAcc  = accountById(draft.debit);
  const creditAcc = accountById(draft.credit);

  // ── Journal actions ───────────────────────────────────────────────────────
  const post = useCallback(() => {
    setError(null);
    const reason = validate(draft);
    if (reason) {
      setError(reasonToHuman(reason));
      emit({ kind: 'txn.rejected', payload: { simId: SIM_ID, reason } });
      return;
    }
    const posted: Posted = { ...draft, ts: Date.now() };
    setEntries((prev) => [...prev, posted]);
    emit({ kind: 'txn.drafted', payload: { simId: SIM_ID, description: draft.description, debitAccount: draft.debit, creditAccount: draft.credit, amount: draft.amount } });
    emit({ kind: 'txn.posted',  payload: { simId: SIM_ID, entryId: posted.id, balanced: true } });
    setDraft(emptyDraft());
  }, [draft, emit]);

  const reset = useCallback(() => {
    setEntries([]);
    setError(null);
    setDraft(emptyDraft());
    setDemoPosted(0);
    setTbConfirmed(new Set());
    setStep(embedded ? 'transactions' : 'journal');
    emit({ kind: 'sim.reset', payload: { simId: SIM_ID } });
  }, [emit, embedded]);

  const loadPreset = useCallback((preset: typeof PRESETS[number]) => {
    setDraft({
      id: crypto.randomUUID(),
      description: preset.description,
      debit: preset.debit,
      credit: preset.credit,
      amount: preset.amount,
    });
    setError(null);
  }, []);

  // ── Trial balance confirmation ─────────────────────────────────────────────
  const confirmTBRow = useCallback((acctId: string) => {
    setTbConfirmed((prev) => new Set([...prev, acctId]));
  }, []);

  const allTBConfirmed = trialBalanceRows.length > 0 && tbConfirmed.size >= trialBalanceRows.length;
  const tbDrTotal = trialBalanceRows.reduce((n, r) => n + r.drBalance, 0);
  const tbCrTotal = trialBalanceRows.reduce((n, r) => n + r.crBalance, 0);
  const tbBalances = tbDrTotal === tbCrTotal && tbDrTotal > 0;

  // ── Step navigation ────────────────────────────────────────────────────────
  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const canGoNext = step !== 'trial-balance';
  const canGoPrev = stepIndex > 0;

  const goNext = () => {
    if (canGoNext) setStep(STEPS[stepIndex + 1]!.id);
  };
  const goPrev = () => {
    if (canGoPrev) setStep(STEPS[stepIndex - 1]!.id);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card className="!p-0 overflow-hidden">
      {/* ── Header ── */}
      <header className={`px-5 py-3 border-b border-ink-200 flex items-center justify-between ${embedded ? 'bg-acct-50' : 'bg-ink-50'}`}>
        <div>
          <div className={`text-xs uppercase tracking-wide font-semibold ${embedded ? 'text-acct-600' : 'text-accent-600'}`}>
            {embedded ? 'Try it yourself' : 'Interactive simulation'}
          </div>
          <h2 className="font-serif text-lg text-ink-900">The Accounting Cycle</h2>
        </div>
        <Button variant="ghost" onClick={reset} aria-label="Reset simulation">↻ Reset</Button>
      </header>

      {/* ── 4-step progress stepper ── */}
      <div className="px-5 py-3 border-b border-ink-200 bg-white">
        <div className="flex items-center gap-0 overflow-x-auto">
          {STEPS.map((s, i) => {
            const isActive = s.id === step;
            const isDone   = i < stepIndex;
            return (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={[
                  'flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150',
                  isActive ? 'bg-acct-500 text-white shadow-sm' : isDone ? 'text-acct-600 hover:bg-acct-50' : 'text-ink-400 hover:bg-ink-50',
                ].join(' ')}
              >
                <span className={[
                  'inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold',
                  isActive ? 'bg-white text-acct-600' : isDone ? 'bg-acct-200 text-acct-700' : 'bg-ink-100 text-ink-400',
                ].join(' ')}>
                  {isDone ? '✓' : i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          STEP 1 — TRANSACTIONS
      ════════════════════════════════════════════════════════════════════ */}
      {step === 'transactions' && (
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-ink-600 leading-relaxed">
            Every business starts with real-world events — buying, selling, paying, receiving. Each event is a <strong>transaction</strong>. Below are four transactions for a small shop. Reveal them one by one, then move to the Journal.
          </p>
          <div className="space-y-2">
            {DEMO_SCENARIO.map((txn, i) => {
              const revealed = i < demoPosted;
              const drAcc = accountById(txn.debit);
              const crAcc = accountById(txn.credit);
              return (
                <div
                  key={txn.id}
                  className={[
                    'rounded-lg border px-4 py-3 text-sm transition-all duration-300',
                    revealed
                      ? 'border-acct-200 bg-acct-50/60'
                      : 'border-ink-200 bg-ink-50/50 opacity-50',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-mono text-xs text-ink-400 mr-2">#{i + 1}</span>
                      <span className={revealed ? 'text-ink-800' : 'text-ink-400 blur-[3px] select-none'}>
                        {txn.description}
                      </span>
                      {revealed && drAcc && crAcc && (
                        <div className="mt-1.5 flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded bg-debit-100/60 text-debit-700 font-medium">Dr: {drAcc.name}</span>
                          <span className="px-2 py-0.5 rounded bg-credit-100/60 text-credit-700 font-medium">Cr: {crAcc.name}</span>
                          <span className="px-2 py-0.5 rounded bg-ink-100 text-ink-600 font-mono">₹{txn.amount.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                    {!revealed && i === demoPosted && (
                      <button
                        onClick={() => setDemoPosted((n) => n + 1)}
                        className="flex-shrink-0 text-xs px-3 py-1 rounded-md border border-acct-300 bg-white text-acct-600 hover:bg-acct-50 font-medium"
                      >
                        Reveal
                      </button>
                    )}
                    {revealed && (
                      <span className="flex-shrink-0 text-acct-500 text-xs font-medium">✓ revealed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {demoPosted < DEMO_SCENARIO.length ? (
            <p className="text-xs text-ink-500 text-center">Reveal all {DEMO_SCENARIO.length} transactions to proceed.</p>
          ) : (
            <div className="pt-2 flex justify-end">
              <button
                onClick={goNext}
                className="px-5 py-2 rounded-xl bg-accent-500 text-ink-900 text-sm font-semibold shadow-card hover:bg-accent-600 hover:text-white transition-colors"
              >
                Next: Journal →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 2 — JOURNAL
      ════════════════════════════════════════════════════════════════════ */}
      {step === 'journal' && (
        <>
          {/* In embedded mode, journal is read-only (derived from demo scenario) */}
          {embedded ? (
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-ink-600 leading-relaxed">
                Each transaction is recorded as a <strong>journal entry</strong> — the debit account comes first, the credit account is indented with "To". Every entry keeps the books balanced: debit total = credit total.
              </p>
              <JournalTable entries={activeEntries} />
              <div className="pt-1 flex justify-between">
                <button onClick={goPrev} className="text-sm text-ink-500 hover:text-ink-800 px-3 py-2">← Transactions</button>
                <button onClick={goNext} className="px-5 py-2 rounded-xl bg-accent-500 text-ink-900 text-sm font-semibold shadow-card hover:bg-accent-600 hover:text-white transition-colors">
                  Next: Ledger →
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Free-play preset bar */}
              <div className="px-5 py-3 border-b border-ink-200 bg-white">
                <div className="text-xs text-ink-500 mb-2">Try one of these transactions:</div>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => loadPreset(p)}
                      className="text-xs px-2.5 py-1.5 rounded-md border border-ink-200 hover:border-accent-500 hover:bg-accent-100/30"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Draft entry form */}
              <div className="px-5 py-4 grid md:grid-cols-[1fr_180px_180px_120px_auto] gap-3 items-end border-b border-ink-200 bg-white">
                <Field label="Description">
                  <input
                    type="text"
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="e.g. Owner brings in cash as capital"
                    className="w-full px-3 py-2 rounded-md border border-ink-300 bg-white text-sm"
                  />
                </Field>
                <Field label="Debit account (Dr)">
                  <AccountPicker value={draft.debit} onChange={(v) => setDraft({ ...draft, debit: v })} />
                </Field>
                <Field label="Credit account (Cr)">
                  <AccountPicker value={draft.credit} onChange={(v) => setDraft({ ...draft, credit: v })} />
                </Field>
                <Field label="Amount (₹)">
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={draft.amount || ''}
                    onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) })}
                    className="w-full px-3 py-2 rounded-md border border-ink-300 bg-white text-sm tabular-nums"
                  />
                </Field>
                <Button onClick={post}>Post entry</Button>
              </div>

              {/* Inline narration */}
              <div className="px-5 py-3 bg-ink-50/50 border-b border-ink-200">
                {error ? (
                  <Callout tone="danger" title="Entry didn't balance">{error}</Callout>
                ) : debitAcc && creditAcc ? (
                  <p className="text-sm text-ink-700">
                    <strong className="text-debit-500">Debit {debitAcc.name}</strong> ({debitAcc.type})
                    {' '}{debitIncreases(debitAcc.type) ? 'increases' : 'decreases'}, and{' '}
                    <strong className="text-credit-500">Credit {creditAcc.name}</strong> ({creditAcc.type})
                    {' '}{debitIncreases(creditAcc.type) ? 'decreases' : 'increases'}.
                  </p>
                ) : (
                  <p className="text-sm text-ink-500">Pick a debit account and a credit account to see the rule that fires.</p>
                )}
              </div>

              {/* Journal table */}
              <div className="px-5 py-4">
                <h3 className="font-serif text-base text-ink-900 mb-2">Journal</h3>
                {activeEntries.length === 0 ? (
                  <p className="text-sm text-ink-500 italic">No entries yet. Post your first transaction above.</p>
                ) : (
                  <JournalTable entries={activeEntries} />
                )}
              </div>

              {/* Step navigation */}
              {activeEntries.length > 0 && (
                <div className="px-5 py-3 border-t border-ink-200 bg-white flex justify-end">
                  <button onClick={goNext} className="px-5 py-2 rounded-xl bg-accent-500 text-ink-900 text-sm font-semibold shadow-card hover:bg-accent-600 hover:text-white transition-colors">
                    Next: Ledger →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 3 — LEDGER
      ════════════════════════════════════════════════════════════════════ */}
      {step === 'ledger' && (
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-ink-600 leading-relaxed">
            Each journal entry is <strong>posted</strong> to the relevant account's T-account in the Ledger. The difference between Debit and Credit sides gives the <em>closing balance</em> for that account.
          </p>
          {Object.keys(ledgers).length === 0 ? (
            <p className="text-sm text-ink-500 italic">
              {embedded ? 'Go back to Transactions and reveal some transactions first.' : 'Post entries in the Journal step first.'}
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(ledgers).map(([acctId, l]) => (
                <TAccount key={acctId} acctId={acctId} ledger={l} />
              ))}
            </div>
          )}
          <div className="pt-1 flex justify-between">
            <button onClick={goPrev} className="text-sm text-ink-500 hover:text-ink-800 px-3 py-2">← Journal</button>
            {Object.keys(ledgers).length > 0 && (
              <button onClick={goNext} className="px-5 py-2 rounded-xl bg-accent-500 text-ink-900 text-sm font-semibold shadow-card hover:bg-accent-600 hover:text-white transition-colors">
                Next: Trial Balance →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 4 — TRIAL BALANCE
      ════════════════════════════════════════════════════════════════════ */}
      {step === 'trial-balance' && (
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-ink-600 leading-relaxed">
            The <strong>Trial Balance</strong> lists every account's closing balance in either the Debit or Credit column — based on its normal balance side. If the books are correct, <em>Total Debits = Total Credits</em>. Confirm each row to reveal the final tally.
          </p>

          {trialBalanceRows.length === 0 ? (
            <p className="text-sm text-ink-500 italic">
              {embedded ? 'Go back to Transactions and reveal some transactions first.' : 'Post entries in the Journal step first.'}
            </p>
          ) : (
            <>
              <table className="w-full text-sm shadow-ledger rounded-md overflow-hidden">
                <thead className="bg-ink-100 text-ink-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Account</th>
                    <th className="text-xs text-ink-400 px-3 py-2 text-left font-normal hidden sm:table-cell">Type</th>
                    <th className="text-right px-3 py-2 w-28 text-debit-600">Dr (₹)</th>
                    <th className="text-right px-3 py-2 w-28 text-credit-600">Cr (₹)</th>
                    <th className="text-center px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {trialBalanceRows.map((row) => {
                    const confirmed = tbConfirmed.has(row.acctId);
                    return (
                      <tr
                        key={row.acctId}
                        className={[
                          'border-t border-ink-200 transition-colors duration-200',
                          confirmed ? 'bg-acct-50/60' : 'bg-white hover:bg-ink-50/50',
                        ].join(' ')}
                      >
                        <td className="px-3 py-2 font-medium text-ink-800">{row.name} A/c</td>
                        <td className="px-3 py-2 text-ink-400 text-xs capitalize hidden sm:table-cell">{row.type}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {row.drBalance > 0 ? (
                            <span className={confirmed ? 'text-debit-600 font-semibold' : 'text-ink-300 blur-[2px] select-none'}>
                              {formatINR(row.drBalance)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {row.crBalance > 0 ? (
                            <span className={confirmed ? 'text-credit-600 font-semibold' : 'text-ink-300 blur-[2px] select-none'}>
                              {formatINR(row.crBalance)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {confirmed ? (
                            <span className="text-acct-500 text-xs font-medium">✓ done</span>
                          ) : (
                            <button
                              onClick={() => confirmTBRow(row.acctId)}
                              className="text-xs px-2.5 py-1 rounded border border-acct-300 text-acct-600 hover:bg-acct-50 font-medium"
                            >
                              Confirm
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals row — only shows once all confirmed */}
                  {allTBConfirmed && (
                    <tr className="bg-ink-100 font-semibold border-t-2 border-ink-300">
                      <td className="px-3 py-2 text-ink-700" colSpan={2}>Totals</td>
                      <td className="px-3 py-2 text-right tabular-nums text-debit-600">{formatINR(tbDrTotal)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-credit-600">{formatINR(tbCrTotal)}</td>
                      <td className="px-3 py-2 text-center">
                        {tbBalances ? (
                          <span className="text-acct-600 font-bold text-sm">✓</span>
                        ) : (
                          <span className="text-credit-500 font-bold text-sm">✗</span>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Balance celebration / error */}
              {allTBConfirmed && tbBalances && (
                <div className="rounded-lg border border-acct-300 bg-acct-50 px-4 py-3 flex items-start gap-3">
                  <span className="text-2xl mt-0.5 select-none" aria-hidden>✅</span>
                  <div>
                    <p className="text-sm font-semibold text-acct-800">It balances!</p>
                    <p className="text-xs text-acct-700 mt-0.5 leading-relaxed">
                      Debit total = Credit total = <strong>{formatINR(tbDrTotal)}</strong>. The accounting equation holds: every rupee debited has a matching rupee credited. Your books are correct.
                    </p>
                  </div>
                </div>
              )}

              {allTBConfirmed && !tbBalances && (
                <Callout tone="danger" title="Doesn't balance — check your entries">
                  Debit total ({formatINR(tbDrTotal)}) ≠ Credit total ({formatINR(tbCrTotal)}). Go back to the Journal and look for a missing or mismatched entry.
                </Callout>
              )}

              {!allTBConfirmed && (
                <p className="text-xs text-ink-500 text-center">
                  Confirm each account ({tbConfirmed.size}/{trialBalanceRows.length}) to see the totals.
                </p>
              )}
            </>
          )}

          <div className="pt-1 flex justify-start">
            <button onClick={goPrev} className="text-sm text-ink-500 hover:text-ink-800 px-3 py-2">← Ledger</button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ─── Sub-components ──────────────────────────────────────────────────────── */

function JournalTable({ entries }: { entries: Posted[] }) {
  return (
    <table className="w-full text-sm shadow-ledger rounded-md overflow-hidden">
      <thead className="bg-ink-100 text-ink-600 text-xs uppercase tracking-wide">
        <tr>
          <th className="text-left px-3 py-2 w-24">No.</th>
          <th className="text-left px-3 py-2">Particulars</th>
          <th className="text-right px-3 py-2 w-28">Debit (₹)</th>
          <th className="text-right px-3 py-2 w-28">Credit (₹)</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, i) => (
          <JournalRow key={e.id} index={i + 1} entry={e} />
        ))}
        <tr className="bg-ink-50 font-medium">
          <td className="px-3 py-2"></td>
          <td className="px-3 py-2 text-right text-ink-500">Totals</td>
          <td className="px-3 py-2 text-right tabular-nums">{formatINR(entries.reduce((n, e) => n + e.amount, 0))}</td>
          <td className="px-3 py-2 text-right tabular-nums">{formatINR(entries.reduce((n, e) => n + e.amount, 0))}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function AccountPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-md border border-ink-300 bg-white text-sm"
    >
      <option value="">— select —</option>
      {ACCOUNTS.map((a) => (
        <option key={a.id} value={a.id}>{a.name}</option>
      ))}
    </select>
  );
}

function JournalRow({ index, entry }: { index: number; entry: Posted }) {
  const dr = accountById(entry.debit)?.name ?? '?';
  const cr = accountById(entry.credit)?.name ?? '?';
  return (
    <tr className="border-t border-ink-200 align-top">
      <td className="px-3 py-2 text-ink-500 font-mono text-xs">#{index}</td>
      <td className="px-3 py-2">
        <div><span className="text-debit-500 font-medium">{dr} A/c</span> <span className="text-ink-500">Dr</span></div>
        <div className="pl-6"><span className="text-ink-500">To</span> <span className="text-credit-500 font-medium">{cr} A/c</span></div>
        <div className="pl-6 text-xs text-ink-500 italic">({entry.description})</div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums animate-post-debit">{formatINR(entry.amount)}</td>
      <td className="px-3 py-2 text-right tabular-nums animate-post-credit">{formatINR(entry.amount)}</td>
    </tr>
  );
}

function TAccount({ acctId, ledger }: { acctId: string; ledger: Ledger }) {
  const acct = accountById(acctId);
  if (!acct) return null;
  const balanceLabel = debitIncreases(acct.type) ? 'Dr balance' : 'Cr balance';
  return (
    <div className="rounded-md border border-ink-200 overflow-hidden bg-white">
      <div className="px-3 py-2 bg-ink-50 border-b border-ink-200 flex justify-between text-xs">
        <span className="font-medium text-ink-900">{acct.name} A/c</span>
        <span className="text-ink-500">{balanceLabel}: <span className="font-mono">{formatINR(Math.abs(ledger.balance))}</span></span>
      </div>
      <div className="grid grid-cols-2 text-xs">
        <div className="border-r border-ink-200 p-2">
          <div className="text-debit-500 font-medium mb-1">Dr</div>
          {ledger.debits.length === 0 && <div className="text-ink-400 italic">—</div>}
          {ledger.debits.map((d, i) => (
            <div key={i} className="flex justify-between tabular-nums">
              <span className="truncate text-ink-600">To {accountById(d.counter)?.name}</span>
              <span>{formatINR(d.amount)}</span>
            </div>
          ))}
        </div>
        <div className="p-2">
          <div className="text-credit-500 font-medium mb-1">Cr</div>
          {ledger.credits.length === 0 && <div className="text-ink-400 italic">—</div>}
          {ledger.credits.map((c, i) => (
            <div key={i} className="flex justify-between tabular-nums">
              <span className="truncate text-ink-600">By {accountById(c.counter)?.name}</span>
              <span>{formatINR(c.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Pure helpers ────────────────────────────────────────────────────────── */

function emptyDraft(): Entry {
  return { id: crypto.randomUUID(), description: '', debit: '', credit: '', amount: 0 };
}

type RejectReason = 'unbalanced' | 'invalid-account' | 'zero-amount' | 'self-loop';

function validate(e: Entry): RejectReason | null {
  if (!e.debit || !e.credit) return 'invalid-account';
  if (e.debit === e.credit)  return 'self-loop';
  if (!(e.amount > 0))       return 'zero-amount';
  return null;
}

function reasonToHuman(r: RejectReason): string {
  switch (r) {
    case 'invalid-account': return 'Pick both a debit (Dr) and a credit (Cr) account.';
    case 'self-loop':       return 'A transaction must affect two different accounts.';
    case 'zero-amount':     return 'Amount must be greater than ₹0.';
    case 'unbalanced':      return 'Debit and credit must be equal.';
  }
}

function buildLedgers(entries: Posted[]): Record<string, Ledger> {
  const out: Record<string, Ledger> = {};
  for (const e of entries) {
    (out[e.debit]  ??= emptyLedger()).debits.push({  entryId: e.id, counter: e.credit, amount: e.amount });
    (out[e.credit] ??= emptyLedger()).credits.push({ entryId: e.id, counter: e.debit,  amount: e.amount });
  }
  for (const acctId of Object.keys(out)) {
    const acct = accountById(acctId);
    const l = out[acctId]!;
    const drTotal = l.debits.reduce((n, x) => n + x.amount, 0);
    const crTotal = l.credits.reduce((n, x) => n + x.amount, 0);
    l.balance = acct && debitIncreases(acct.type) ? drTotal - crTotal : crTotal - drTotal;
  }
  return out;
}

function emptyLedger(): Ledger { return { debits: [], credits: [], balance: 0 }; }

/**
 * Build trial balance rows from ledger closing balances.
 * Each account's closing balance goes into the Debit column if Dr-natural, else Credit column.
 */
function buildTrialBalance(ledgers: Record<string, Ledger>): TBRow[] {
  return Object.entries(ledgers).map(([acctId, l]) => {
    const acct = accountById(acctId);
    const name = acct?.name ?? acctId;
    const type = acct?.type ?? '';
    const bal = Math.abs(l.balance);
    const isDrNatural = acct ? debitIncreases(acct.type) : true;
    return {
      acctId,
      name,
      type,
      drBalance: isDrNatural ? bal : 0,
      crBalance: isDrNatural ? 0 : bal,
    };
  });
}

function formatINR(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
